import React from "react";
import { useMutation, gql } from "@apollo/client";
import { Form } from "react-final-form";
import { Price } from "@syos-music/common";
import { TextField } from "@syos-music/form";

import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Delete as DeleteIcon } from "mdi-material-ui";

import OrderSidebar from "../index";

export default function DiscountCode({ order }) {
  const [removeDiscountCode, { loading: removingDiscountCode }] = useMutation(
    React.useMemo(
      () =>
        gql`
          mutation ($orderId: ID!) {
            RemoveOrderDiscountCode(orderId: $orderId) {
              order {
                id

                ...OrderSidebar_order
              }
            }
          }

          ${OrderSidebar.fragments.order}
        `,
      []
    ),
    { variables: { orderId: order.id } }
  );

  const [addDiscountCode] = useMutation(
    React.useMemo(
      () => gql`
        mutation ($orderId: ID!, $code: String!) {
          AddOrderDiscountCode(orderId: $orderId, code: $code) {
            order {
              id
              ...OrderSidebar_order
            }
          }
        }

        ${OrderSidebar.fragments.order}
      `,
      []
    )
  );

  const handleSubmit = React.useCallback(
    ({ code }, form) => {
      addDiscountCode({ variables: { orderId: order.id, code } })
        .then(() => {
          form.reset();
        })
        .catch(() => true);
    },
    [addDiscountCode, order.id]
  );

  if (order.discountCode)
    return (
      <Grid container spacing={0} alignItems="center">
        {!order.paymentId && (
          <Grid item>
            <IconButton
              size="small"
              onClick={removeDiscountCode}
              disabled={removingDiscountCode}
            >
              {removingDiscountCode ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Grid>
        )}

        <Grid item xs component={Typography}>
          Réduc{" "}
          <Box
            component="span"
            sx={{
              bgcolor: (t) => t.palette.background.odd,
              padding: "0 0.5ch",
              fontFamily: "'Roboto Mono', monospace",
            }}
          >
            {order.discountCode.code}
          </Box>
        </Grid>

        <Grid item component={Typography}>
          <strong>
            <Price {...order.discountAmount} taxRate={order.taxRate} />
          </strong>
        </Grid>

        {order.discountCode.invoice && (
          <>
            <Grid
              item
              xs={12}
              component={Typography}
              style={{ marginBottom: 8 }}
            >
              <small>
                Facture bon cadeau&nbsp;:&nbsp;
                <a
                  href={order.discountCode.invoice.file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {order.discountCode.invoice.reference}
                </a>
              </small>
            </Grid>

            {order.discountCode.invoice.creditNotes.map((creditNote) => (
              <Grid item xs={12} component={Typography} key={creditNote.id}>
                <small>
                  Avoir bon cadeau&nbsp;:&nbsp;
                  <a
                    href={creditNote.file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {creditNote.reference}
                  </a>
                </small>
              </Grid>
            ))}
          </>
        )}
      </Grid>
    );

  if (!order.paymentId)
    return (
      <Form onSubmit={handleSubmit}>
        {({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <Grid container spacing={1}>
              <Grid item xs>
                <TextField
                  name="code"
                  placeholder="Code de réduction"
                  required
                />
              </Grid>

              <Grid item>
                <LoadingButton
                  type="submit"
                  loading={submitting}
                  disabled={invalid}
                  size="small"
                >
                  Ajouter
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Form>
    );

  return null;
}

DiscountCode.fragments = {
  order: gql`
    fragment OrderSidebar_Cart_DiscountCode_order on Order {
      id
      paymentId

      taxRate

      discountAmount {
        currency
        value
      }

      discountCode {
        id
        code

        ... on DiscountCode_Gift {
          invoice {
            id
            reference
            file {
              id
              url
            }

            creditNotes {
              id
              reference
              file {
                id
                url
              }
            }
          }
        }
      }
    }
  `,
};
